import HttpRequest from '@/config/axios.config'
import { isEmptyValue } from '@/utils/common'
import { ITimeAndActionPayload } from '../interface/order.interface'
import { get, has, isEmpty, join, pick } from 'lodash'

export async function orderHeroRef(id: string) {
  return await HttpRequest.get(`/orders/${id}/sections/hero-summaries`)
}

export async function orderSectionsRef(id: string) {
  return await HttpRequest.get(`/orders/${id}/sections`)
}

export async function createTimeAndActionRef(payload: ITimeAndActionPayload) {
  return await HttpRequest.post(`time-actions`, payload)
}

export async function updateTimeAndActionRef(
  payload: ITimeAndActionPayload,
  timeActionId: string
) {
  return await HttpRequest.put(`time-actions/${timeActionId}`, payload)
}

export async function orderDetailRef(id: string) {
  return await HttpRequest.get(`orders/${id}`)
}

export async function collaboratorsRef(id: string) {
  return await HttpRequest.get(`orders/${id}/users`)
}

export async function searchUserByEmailRef(email: string) {
  return await HttpRequest.get(`/users`, { params: { email } })
}

export async function updateCollaboratorRef(id: string, data: any) {
  return await HttpRequest.post(`orders/${id}/assignment`, data)
}

export async function updateCollaboratorRoleRef(id: string, data: any) {
  return await HttpRequest.put(`orders/${id}/assignments`, data)
}

export async function taskCollaboratorsRef(id: string) {
  return await HttpRequest.get(`tasks/${id}/assignments`)
}

export const updateTaskStatus = async (id: string, task: any) => {
  return await HttpRequest.put(`/tasks/${id}/status`, task)
}

export const updateTaskPlanDate = async (id: string, task: any) => {
  await HttpRequest.put(`/tasks/${id}/plandate`, task)
}

export const updateTaskAssigneeRef = async (taskId: string, payload: any) => {
  return await HttpRequest.put(`/tasks/${taskId}/assign`, payload)
}

export const orderAndDocumentCountRef = async (taskId) => {
  return await HttpRequest.get(`/tasks/${taskId}/task-document-count`)
}

export const removeCollaboratorRef = async (orderId: string, payload: any) => {
  return await HttpRequest.post(
    `/orders/${orderId}/remove/assignments`,
    payload
  )
}

export const createOrder = async (payload) => {
  return await HttpRequest.post('/orders', payload).catch(
    (error) => error?.response
  )
}

export const updateOrderDetail = async (payload) => {
  return await HttpRequest.put(`/orders/${payload.id}`, payload).catch(
    (error) => error?.response
  )
}

export const orderAdvanceSummary = async (payload: any = {}) => {
  const defaultValue = {
    totalOrders: 0,
    activeOrders: 0,
    urgent: 0,
    warning: 0,
    onTrack: 0,
    delivered: 0,
  }
  try {
    const {
      data: { data = defaultValue },
    } = await HttpRequest.post(`/orders/advance-search-summary`, {
      ...payload,
      ...(payload.delivery_date && {
        delivery_date: join(payload.delivery_date, ','),
      }),
    })

    return data
  } catch (error) {
    return defaultValue
  }
}

// TODO: REMOVE
export const orderSummary = async (queryParams = {}) => {
  const searchTerm = get(queryParams, 'searchTerm', '')
  const defaultValue = {
    totalOrders: 0,
    activeOrders: 0,
    urgent: 0,
    warning: 0,
    onTrack: 0,
    delivered: 0,
  }

  try {
    if (!isEmpty(searchTerm)) {
      const {
        data: { data = defaultValue },
      } = await HttpRequest.get(`/orders/search-summary`, {
        params: queryParams,
      })
      return data
    } else {
      const {
        data: { data = defaultValue },
      } = await HttpRequest.get('/orders/summary')
      return data
    }
  } catch (error) {
    return defaultValue
  }
}

export const orderSectionSummary = async (id: string) => {
  return await HttpRequest.get(`/orders/${id}/section-summaries`)
}

export const getSupplierRef = async (company_id = '') => {
  return await HttpRequest.get(`/suppliers`, {
    params: {
      status: 'active',
      all: true,
      type: 'supplier',
      ...(!isEmptyValue(company_id) && { company_id }),
    },
  })
}

export const getFactoryRef = async (company_id = '') => {
  return await HttpRequest.get(`/suppliers`, {
    params: {
      status: 'active',
      type: 'factory',
      all: true,
      ...(!isEmptyValue(company_id) && { company_id }),
    },
  })
}

export const getOrderSettingRef = async (company_id) => {
  return await HttpRequest.get(`/company-settings?name=order`, {
    params: { company_id },
  })
}

export const getAddOrderSettingRef = async (company_id) => {
  try {
    const {
      data: { data = [] },
    } = await HttpRequest.get(
      `/company-settings?name=allow_new_order&status=active`,
      {
        params: { company_id },
      }
    )

    if (isEmpty(data)) {
      return null
    }
    const result = pick(data[0].value, ['roles'])
    return result
  } catch (error) {
    console.log(error)
  }
}
export const getOrderItemSettingRef = async (company_id) => {
  return await HttpRequest.get(
    `/company-settings?name=order_item&status=active&type=order`,
    {
      params: { company_id },
    }
  )
}

export const getOrderSequentialByEntityService = async (params: {
  company_id: string
  entity_type: string
}) => {
  try {
    if (!has(params, 'company_id') || !has(params, 'entity_type')) {
      throw new Error('Get Order Sequential Number Invalid Params')
    }

    const { data } = await HttpRequest.get('/orders/sequential_numbers', {
      params,
    })

    return data
  } catch (error) {
    console.error('Failed fetch sequential number :', error)
    return []
  }
}

export const getOrderColumnRef = async () => {
  try {
    const { data: data = [] } = await HttpRequest.get(
      `/company-settings?name=custom_columns`
    )
    return data
  } catch (error) {
    console.log(error)
  }
}

export const getPropertyOrders = (params: {
  key: string
  value: string
  data_type: string
}) => HttpRequest.get(`/orders/property`, { params })

export async function getOrderTemplates() {
  return HttpRequest.get('/orders/template')
}

export async function getAllOrderPropertyRef() {
  return await HttpRequest.get('/properties')
}

export async function createPropertyOrderPropertyRef(data: any) {
  return await HttpRequest.post('/properties', data)
}

export async function createPropertyValueRef(id: any, data: any) {
  return await HttpRequest.post(`/properties/${id}/property-values`, data)
}

export async function createTimeActionTemplateRef(data: any) {
  return await HttpRequest.post('/templates', data)
}

export async function updateTimeActionTemplateRef(id: string, data: any) {
  return await HttpRequest.put(`/templates/${id}`, data)
}

export async function getTimeActionTemplateRef() {
  return await HttpRequest.get('/templates?type=4')
}

export async function getOrderTemplateRef(data?: any) {
  return await HttpRequest.get('/templates?type=2', { params: data })
}

export async function createOrderTemplateRef(data: any) {
  return await HttpRequest.post('/templates', data)
}

export async function updateOrderTemplateRef(id, data: any) {
  return await HttpRequest.put(`/templates/${id}`, data)
}

export async function downloadPomExcelRef(body: any) {
  return await HttpRequest.post(`/orders/download/pom`, body, {
    responseType: 'arraybuffer',
  })
}

export async function downloadOrderTemplateExcelRef(body: any) {
  return await HttpRequest.post(`/orders/download/order`, body, {
    responseType: 'arraybuffer',
  })
}

export async function uploadPomExcelRef(body: any) {
  return await HttpRequest.post(`/orders/upload/pom`, body).catch(
    (error) => error.response
  )
}

export async function uploadOrderExcelRef(body: any) {
  return await HttpRequest.post(`/orders/upload/order`, body, {
    responseType: 'arraybuffer',
  }).catch((error) => error.response)
}

export async function getAllDefectsGroup() {
  return await HttpRequest.get('/defect-groups?all=true')
}
